import { saveAs } from 'file-saver';
import _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Button,
  DateTime,
  FiltersBar,
  Icon,
  ListView,
  ListViewActions,
  MultiSelect,
  Page,
  SearchInput,
  SingleSelectFilter,
  Tooltip,
} from '~/components';
import { useApi, useWorkspace } from '~/contexts';
import { useDocumentTitle, useNumberFormat } from '~/hooks';
import { ErrorPage, PageLoader } from '~/routes/public/pages';
import { colors } from '~/styles';
import { dateFormats, getIconFromMIME, mimeTypes } from '~/utils';

export default function ReportExportsPage() {
  useDocumentTitle('Report Exports');
  const [{ data, isReady, isFetching }, setQuery] = useState({ data: null, isReady: false, isFetching: false });

  const api = useApi();
  const { workspace } = useWorkspace();

  const [params, setParams] = useState({
    q: '',
    exportStatusId: [
      { id: 'processing', name: 'Processing' },
      { id: 'completed', name: 'Completed' },
    ],
    exportTriggerId: null,
  });

  const handleFilter = ({ target: { name, value } }) => {
    setQuery((query) => ({ ...query, isFetching: true }));
    setParams((params) => ({ ...params, [name]: value }));
  };

  const fetchData = useCallback(async () => {
    const { data } = await api.www
      .workspaces(workspace.id)
      .reports()
      .exports({
        q: params.q ?? undefined,
        exportStatusId: params.exportStatusId?.map(({ id }) => id),
        exportTriggerId: params.exportTriggerId ?? undefined,
      });
    setQuery({ data, isReady: true, isFetching: false });
  }, [api, workspace.id, params]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const location = useLocation();
  const history = useHistory();
  useEffect(() => {
    if (!data) return;

    const urlParams = new URLSearchParams(location.search);
    const id = urlParams.get('downloadId');
    if (!id) return;

    const file = data.find((file) => file.id === id);
    if (file) {
      saveAs(file.url, file.name);
    }
    history.push(location.pathname);
  }, [data, location.pathname, location.search, history]);

  const numberFormat = useNumberFormat({ minimumFractionDigits: 0, maximumFractionDigits: 0 });

  if (!isReady) return <PageLoader />;
  if (!data) return <ErrorPage.NotFound publicSite={false} />;

  return (
    <Page>
      <Page.Header>
        <Page.Info>
          <Page.Eyebrow>Reports</Page.Eyebrow>
          <Page.Title>Exports</Page.Title>
        </Page.Info>
      </Page.Header>

      <Page.Filters>
        <FiltersBar>
          <SearchInput name="q" onChange={handleFilter} placeholder="Search" />

          <MultiSelect.Filter
            icon="filter"
            name="exportStatusId"
            value={params.exportStatusId}
            renderValue={(value) => value.map((v) => v.name).join('; ')}
            options={[
              { id: 'pending', name: 'Pending' },
              { id: 'processing', name: 'Processing' },
              { id: 'completed', name: 'Completed' },
              { id: 'failed', name: 'Failed' },
              { id: 'expired', name: 'Expired' },
            ]}
            placeholder="Status"
            onChange={handleFilter}
          />

          <SingleSelectFilter
            icon="filter"
            name="exportTriggerId"
            value={params.exportTriggerId}
            options={[
              { id: 'manual', name: 'Manual' },
              { id: 'schedule', name: 'Schedule' },
            ]}
            placeholder="Trigger"
            onChange={handleFilter}
          />
        </FiltersBar>
      </Page.Filters>

      <Page.ListView>
        <ListView>
          <ListView.Header>
            <ListView.Column width="2rem" />
            <ListView.Column sticky minWidth="10rem">
              Title
            </ListView.Column>
            <ListView.Column width="7rem">Status</ListView.Column>
            <ListView.Column>Trigger</ListView.Column>
            <ListView.Column width="5rem" align="right">
              Rows
            </ListView.Column>
            <ListView.Column align="right" width="12rem">
              Created
            </ListView.Column>
            <ListView.Column align="right" width="12rem">
              Expires
            </ListView.Column>

            <ListViewActions.Column />
          </ListView.Header>

          <ListView.Body fade={isFetching}>
            {data?.map((file) => {
              return (
                <ListView.Row key={file.id}>
                  <ListView.Cell>
                    <Tooltip
                      message={
                        {
                          [mimeTypes.csv]: 'CSV format',
                          [mimeTypes.xlsx]: 'Excel format',
                          [mimeTypes.pdf]: 'PDF format',
                          [mimeTypes.iif]: 'IIF format',
                        }[file.type]
                      }
                      style={{ width: '1.5rem' }}>
                      <Icon color={colors.primary} icon={getIconFromMIME(file.type)} spaceRight />{' '}
                    </Tooltip>
                  </ListView.Cell>

                  <ListView.Cell>{file.title}</ListView.Cell>

                  <ListView.Cell>{file.status}</ListView.Cell>

                  <ListView.Cell>{file.reportScheduleId ? 'Schedule' : 'Manual'}</ListView.Cell>

                  <ListView.Cell>{_.isNumber(file.rowCount) && numberFormat.format(file.rowCount)}</ListView.Cell>

                  <ListView.Cell>
                    <DateTime value={file.createdAt} format={dateFormats.dateTime} />
                  </ListView.Cell>

                  <ListView.Cell>
                    {file.expiresAt && <DateTime value={file.expiresAt} format={dateFormats.dateTime} />}
                  </ListView.Cell>

                  <ListViewActions>
                    <Button
                      disabled={file.statusId !== 'completed' || !file.url}
                      onClick={() => {
                        saveAs(file.url, file.name);
                      }}>
                      <Icon icon="download" />
                    </Button>
                  </ListViewActions>
                </ListView.Row>
              );
            })}

            {data?.length === 0 && <ListView.Empty />}
          </ListView.Body>

          <ListView.Status total={data?.length} label="Exports" isLoading={isFetching} />
        </ListView>
      </Page.ListView>
    </Page>
  );
}
